import { useContext, useMemo } from 'react';
import RfsLayout from '../../../components/layouts/RfsLayout';
import { CostingRequestsContext } from '../../../contexts/CostingRequest';
import ContentPaper from '../../../components/layouts/rfs/Content';
import MantineTable from '../../../common/table/mantine';
import { Button, Container, Grid, Space, Text } from '@mantine/core';
import { CustomSelect, CustomTextInput } from '../styles';
import { Provider, StockPriceContext } from './context';
import { AppLoader } from '../../../components/common/loader';
import ExtensionPlants from './plants';
import { IconChevronLeft } from '@tabler/icons-react';
import styled from '@emotion/styled';

const StyledBackButton = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;

  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const StyledContainer = styled(Container)`
  margin: 0;
  display: flex;
  border-left: 2px solid rgb(244, 224, 15);
`;

export const skuColumns = [
  {
    accessorKey: 'sku',
    header: 'SKU No',
  },
  {
    accessorKey: 'sku_description',
    header: 'SKU Description',
  },
  {
    accessorKey: 'brand',
    header: 'Brand',
  },
  {
    accessorKey: 'sub_brand',
    header: 'Sub Brand',
  },
  {
    accessorKey: 'returnability',
    header: 'Returnability',
  },
  {
    accessorKey: 'container',
    header: 'Container',
  },
  {
    accessorKey: 'container_size',
    header: 'Container size',
    Cell: ({ row }) =>
      row.original.container_vol ? row.original.container_vol : row.original.container_size,
  },
];

export const Header = ({ heading = 'STOCK PRICE EXTENSTION', backAction = null }) => (
  <>
    <Space h='md' />
    {backAction ? (
      <StyledBackButton onClick={backAction}>
        <IconChevronLeft /> Back
      </StyledBackButton>
    ) : (
      <StyledContainer>
        <Text fz='md' fw={700}>
          {heading}
        </Text>
      </StyledContainer>
    )}
    <Space h='md' />
  </>
);

const Content = () => {
  const { skus, loaders } = useContext(CostingRequestsContext);
  const {
    selectedSku,
    setSelectedSku,
    countries,
    salesOrgs,
    form,
    setForm,
    fetchPlants,
    plantsLoading,
    plants,
    createExtensionRequest,
    submitLoading,
    reset,
  } = useContext(StockPriceContext);

  const columns = useMemo(() => skuColumns, [skus]);

  const handleRowSelection = (data) => {
    let currentValues = { ...selectedSku };
    currentValues = { ...data };
    const newValue = Object.keys(currentValues)?.[0];
    const index = skus.findIndex((sku) => parseInt(sku.sku) === parseInt(newValue));

    setForm({ ...form, material: newValue, material_description: skus[index]?.sku_description });
    setSelectedSku(currentValues);
  };

  const isDisabled = () => {
    return !form.material || !form.country_code || !form.salesorg;
  };

  const hasPlants = () => plants.length > 0;

  const Action = () => {
    const hasEditablePlants = plants.some((p) => p.isEditable);
    return (
      <Grid justify='flex-end'>
        <Grid.Col span={2}>
          <Button
            variant='gradient'
            color='primary'
            fullWidth
            disabled={
              (hasPlants() && !hasEditablePlants) || isDisabled() || plantsLoading || submitLoading
            }
            onClick={() => (hasPlants() ? createExtensionRequest() : fetchPlants())}
          >
            {plantsLoading || submitLoading ? (
              <AppLoader size='sm' />
            ) : hasPlants() ? (
              'Submit'
            ) : (
              'Confirm Selection'
            )}
          </Button>
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <RfsLayout>
      <ContentPaper page='stock-price-extension' footer={<Action />}>
        <Header heading='STOCK PRICE EXTENSION' backAction={hasPlants() ? reset : null} />

        {hasPlants() ? (
          <ExtensionPlants />
        ) : (
          <>
            <Grid justify='flex-end'>
              <Grid.Col span={6}>
                <CustomSelect
                  name='country_code'
                  value={form.country_code || null}
                  label='Select Country'
                  placeholder='Select Country'
                  data={countries}
                  onChange={(value) => {
                    const org = salesOrgs.filter((org) => org.includes(value));
                    const country = countries.find((country) => country.value === value);
                    setForm({
                      ...form,
                      country_code: value,
                      salesorg: org[0],
                      country: country.label,
                    });
                  }}
                  searchable
                  withAsterisk
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <CustomTextInput
                  name='salesorg'
                  readOnly={true}
                  value={form.salesorg || null}
                  label='Sales Org'
                  placeholder='Sales Org'
                  withAsterisk
                />
              </Grid.Col>
            </Grid>

            <Space h='md' />
            <MantineTable
              columns={columns}
              initialData={skus}
              unique={'sku'}
              handleRowSelection={handleRowSelection}
              activeRows={selectedSku}
              enableRowSelection={true}
              loading={loaders.skus}
              hideSelectColumn={false}
              showResetAll={false}
              fullScreen={false}
              multiRowSelection={false}
              showSelectedToggle={true}
              pageSize={10}
            />
          </>
        )}
      </ContentPaper>
    </RfsLayout>
  );
};

const SelectSKU = () => {
  return (
    <Provider>
      <Content />
    </Provider>
  );
};

export default SelectSKU;
